import { useEffect, useState } from "react";
import styled from "styled-components";

import {
  Container,
  FormControlLabel,
  Grid,
  Box,
  Radio,
  RadioGroup,
  Typography,
  Button,
  CircularProgress,
  Checkbox,
  Alert,
} from "@mui/material";

// * form
import { useForm } from "react-hook-form";

// * services
import { postPaymentTokenization } from "../services/payments";

// components
import CardList from "./CardList";
import BankForm from "./BankForm";
import SharedModal from "./shared/SharedModal";
import RequiestStatusModal from "./modals/RequiestStatusModal";
import { emailRegex, redirectPage } from "../utils/misc";

// hooks
import { useDeviceId } from "../hooks/useDeviceId";
import SignaturesListModal from "./modals/SignaturesListModal";
import { useAuthToken } from "../hooks/useAuthToken";
import { useTranslation } from "react-i18next";
import { LogoBody } from "assets/logos";
import { AthleticLogo } from "assets/icons/config";

const Main = styled(Container)`
  padding: 30px;
  .customerInfo {
    background: #f7f7f7;
    border-radius: 10px;
  }
`;

const errorList = {
  130: "Error en el método de pago",
  109: "Error en datos del customer",
  110: "Error en datos del customer",
  111: "Error en datos del customer",
  112: "Error en datos del customer",
  113: "Error en datos del customer",
  114: "Error en datos del customer",
  119: "La tarjeta es requerida",
  120: "Tarjeta no encontrada",
  121: "La tarjeta es invalida",
  122: "Datos de la tarjeta incorrectos",
  129: "Limite de tarjetas permitidas excedido",
  140: "Titular de la tarjeta invalido",
};

export default function TokenizationForm({
  customerId,
  customerAgreements,
  bankList,
}) {
  const [option, setOption] = useState("card");

  // credit card
  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cvc, setCvc] = useState("");
  const [expiry, setExpiry] = useState("");
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");
  const [errorCollection, setErrorCollection] = useState([]);
  const [translatedCollection, setTranslatedCollection] = useState([]);
  const [emailError, setEmailError] = useState(null);
  const [serverResponse, setServerResponse] = useState([]);
  const [openSignatureListModal, setOpenSignatureListModal] = useState(false);

  // *  bank form
  const [selectedAccount, setSelectedAccount] = useState(false);

  // *  card form
  const [selectedCard, setSelectedCard] = useState(false);

  const [disableSubmit, setDisableSubmit] = useState(true);

  const [cardErrors, setCardErrors] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  const { t } = useTranslation();

  const currentBrand = localStorage.getItem("current_brand");

  const { getDeviceId } = useDeviceId();

  const { getAuthToken } = useAuthToken();

  useEffect(() => {
    reset("");
  }, [option]);

  useEffect(() => {
    if (errorCollection.length > 0) {
      const errors = [];
      errorCollection?.map((item) => {
        if (item.message.message) {
          errors.push({
            message: errorList[item.message.message.replace(/\D/g, "")]
              ? errorList[item.message.message.replace(/\D/g, "")]
              : "No se pudo validar el método de pago",
          });
          return;
        }
        errors.push({
          message: item.message,
        });
      });
      setTranslatedCollection(errors);
    }
  }, [errorCollection]);

  useEffect(() => {
    if (
      (cardErrors.length > 0 ||
        cardNumber.length === 0 ||
        cvc.length === 0 ||
        expiry.length === 0) &&
      option == "card"
    ) {
      setDisableSubmit(true);
    } else {
      setDisableSubmit(false);
    }
  }, [cardErrors, option]);

  const handleChange = (event) => {
    setOption(event.target.value);
  };

  const pushError = (key, newError) => {
    if (!cardErrors.includes(key) && newError) {
      setCardErrors([...cardErrors, key]);
    } else {
      const filteredErrors = cardErrors.filter((err) => err !== key);
      setCardErrors(filteredErrors);
    }
  };

  const onSubmit = async (data) => {
    if (!localStorage.getItem("access_token_bt")) {
      getAuthToken();
      return;
    }
    let submitData = {
      customer_id: customerId,
      agreement_lines_ids: customerAgreements.agreement_lines,
    };

    // ! card payment
    if (option === "card") {
      if (selectedCard) {
        submitData = {
          ...submitData,
          ...selectedCard,
          type: "token_card",
        };
        submitData["card_number"] = selectedCard.account;
      } else {
        submitData = {
          ...submitData,
          type: "token_card",
          name: data.name,
          number_document: data.number_document,
          type_document: data.type_document,
        };
        const expladeExpiryCard = expiry.split("/");
        submitData["name"] = cardName;
        submitData["expiration_month"] = expladeExpiryCard[0].replace(
          /\s/g,
          ""
        );
        submitData["security_code"] = cvc;
        submitData["expiration_year"] =
          "20" + expladeExpiryCard[1].replace(/\s/g, "");
        submitData["card_number"] = cardNumber;
      }

      // ! bank payment
    } else if (option === "bank") {
      if (!emailRegex.test(data.email)) {
        setEmailError("Correo invalido");
        return;
      }

      if (selectedAccount) {
        submitData = {
          ...submitData,
          banks_id: selectedAccount.bank_id,
          account: selectedAccount.account,
          account_type: selectedAccount.account_type,
          type: "bank_account",
        };
      } else {
        submitData = {
          ...submitData,
          banks_id: data.banks_id,
          account: data.account,
          account_type: data.account_type,
          type: "bank_account",
          name: `${data?.name?.trim()} ${data?.first_last_name?.trim()} ${data?.second_last_name?.trim()}`
            .toUpperCase()
            .trim(),
          last_name: `${data?.first_last_name?.trim()}`.toUpperCase(),
          number_document: data.number_document,
          type_document: data.type_document,
          mail: data.email,
        };
      }
    }

    submitData["device_id"] = getDeviceId();

    setIsLoading(true);
    try {
      const response = await postPaymentTokenization(submitData);
      if (response.data.data.urls_signature) {
        setCurrentStatus("MÉTODO DE PAGO AGREGADO");
        setServerResponse(response.data.data.urls_signature);
        setOpenSignatureListModal(true);
        // redirectPage(
        //   currentBrand === "1"
        //     ? "https://bodytech.com.co/"
        //     : "https://athleticgym.com.co/"
        // );
      } else {
        const message = response?.data?.message?.length
          ? response?.data?.message[0]?.message ?? response?.message[0]?.message
          : t("Tokenization.Rejected");
        setCurrentStatus(message);
        setErrorCollection(response.data.message);
        setShowStatusModal(true);
      }
    } catch (err) {
      const message = err.response?.data?.message?.length
        ? err.response?.data?.message[0]?.message ?? err.response?.message[0]?.message
        : t("Tokenization.Rejected");

      setCurrentStatus(message);
      setShowStatusModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const onError = () => {};

  return (
    <>
      <Main
        style={{
          maxWidth: 400,
          minHeight: "100vh",
          border: "1px solid #ccc",
          boxShadow: "0px 0px 10px 1px #ccc",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box
                display={"flex"}
                justifyContent={"center"}
                style={{ margin: "auto" }}
              >
                {["1", "3"].includes(currentBrand) ? (
                  <LogoBody />
                ) : (
                  <AthleticLogo />
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box display={"flex"} justifyContent={"center"}>
                <Typography
                  variant={"h5"}
                  style={{ fontSize: 17 }}
                  color={"primary"}
                >
                  {"Inscripción de Método de Pago"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box display={"flex"} justifyContent={"center"}>
                <RadioGroup onChange={handleChange} value={option} row>
                  <FormControlLabel
                    value="card"
                    control={<Radio size="small" />}
                    label={
                      <Typography style={{ fontSize: 12 }}>
                        {"CRÉDITO"}
                      </Typography>
                    }
                  />

                  {currentBrand !== "3" && (
                    <FormControlLabel
                      value="bank"
                      control={<Radio size="small" />}
                      label={
                        <Typography style={{ fontSize: 12 }}>
                          {"AHORROS/CORRIENTE"}
                        </Typography>
                      }
                    />
                  )}
                </RadioGroup>
              </Box>
            </Grid>
            <Grid item xs={12}>
              {option === "card" ? (
                <CardList
                  data={customerAgreements}
                  control={control}
                  cvc={cvc}
                  setCvc={setCvc}
                  expiry={expiry}
                  setExpiry={setExpiry}
                  cardNumber={cardNumber}
                  setCardNumber={setCardNumber}
                  setCardName={setCardName}
                  setSelectedCard={setSelectedCard}
                  selectedCard={selectedCard}
                  reset={reset}
                  pushError={pushError}
                  errors={errors}
                />
              ) : (
                <BankForm
                  data={customerAgreements}
                  control={control}
                  customerId={customerId}
                  bankList={bankList}
                  setSelectedAccount={setSelectedAccount}
                  selectedAccount={selectedAccount}
                  reset={reset}
                  errors={errors}
                  emailError={emailError}
                  setEmailError={setEmailError}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Box display={"flex"} justifyContent={"center"}>
                <FormControlLabel
                  style={{ maxWidth: 400 }}
                  label={
                    <Typography
                      style={{
                        fontSize: 13,
                        fontFamily: "Gotham Medium",
                        textDecoration: "underline",
                      }}
                    >
                      <b>
                        {
                          "Estoy aceptando el debito mensual de este medio de pago."
                        }
                      </b>
                    </Typography>
                  }
                  control={
                    <Checkbox size="small" checked={true} name="checked" />
                  }
                />
              </Box>
            </Grid>
            {option === "card" && (
              <Grid item xs={12}>
                <Alert severity="warning" style={{ fontSize: "0.8rem" }}>
                  Recuerda que no podrás inscribir una tarjeta débito o prepago,
                  solo podrás inscribir una tarjeta crédito o una cuenta
                  bancaria.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box display={"flex"} justifyContent={"center"} mt={1}>
                <Button
                  type="submit"
                  disabled={disableSubmit || isLoading}
                  variant="contained"
                  fullWidth
                  color="primary"
                  style={{ maxWidth: 400 }}
                >
                  {isLoading ? (
                    <CircularProgress size={30} color="primary" />
                  ) : (
                    <span>{"Guardar"}</span>
                  )}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box display={"flex"} justifyContent={"center"}>
                <Typography style={{ fontSize: 13 }} align="center">
                  {"Todas las transacciones se realizan de forma segura."}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </form>
        {showStatusModal && (
          <SharedModal
            isOpen={showStatusModal}
            modalBody={
              <Box display={"flex"} justifyContent={"center"}>
                <RequiestStatusModal
                  handleClose={setShowStatusModal}
                  requestStatus={currentStatus}
                  errorCollection={translatedCollection}
                />
              </Box>
            }
          />
        )}

        {openSignatureListModal && (
          <SharedModal
            isOpen={openSignatureListModal}
            modalBody={
              <Box display={"flex"} justifyContent={"center"}>
                <SignaturesListModal data={serverResponse} />
              </Box>
            }
          />
        )}
      </Main>
    </>
  );
}
